import type {Repository} from '@github-ui/current-repository'
import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {SortAscIcon, SortDescIcon, TrashIcon, VerifiedIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ConfirmationDialog, Heading, Link, Pagination} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'

import type {Attestation, SortOrderType} from '../types'
import {SortOrder} from '../types'
import {AttestationListItem} from './AttestationListItem'
import styles from './AttestationsList.module.css'

type AttestationsListProps = {
  repo: Repository
  attestations: Attestation[]
  totalCount: number
  onSortChange: (order: SortOrderType) => void
  onPageChange: (pageIndex: number) => void
  currentSort: SortOrderType
  pageCount: number
  currentPage: number
  selectedAttestationIds?: Set<string>
  onAttestationSelectionChange?: (ids: Set<string>) => void
  onDelete?: (selectedIds: string[]) => void
  hasWritePermission: boolean
}

export function AttestationsList({
  repo,
  attestations,
  totalCount,
  onSortChange,
  onPageChange,
  currentSort,
  pageCount,
  currentPage,
  selectedAttestationIds,
  onAttestationSelectionChange,
  onDelete,
  hasWritePermission,
}: AttestationsListProps) {
  const [isDialogOpen, setDialogOpen] = useState(false)
  const canDeleteAttestations = hasWritePermission

  const onAttestationItemSelect = useCallback(
    (attestationId: string) => {
      const update = new Set(selectedAttestationIds || [])
      if (selectedAttestationIds?.has(attestationId)) {
        update.delete(attestationId)
      } else {
        update.add(attestationId)
      }
      onAttestationSelectionChange?.(update)
    },
    [selectedAttestationIds, onAttestationSelectionChange],
  )

  const handleDialogClose = (gesture: 'confirm' | 'cancel' | 'close-button' | 'escape') => {
    if (gesture === 'confirm') {
      onDelete?.(Array.from(selectedAttestationIds || []))
      onAttestationSelectionChange?.(new Set())
    }
    setDialogOpen(false)
  }

  if (attestations.length === 0) {
    return <BlankSlate />
  }

  const formatNumber = (num: number): string => {
    return Math.abs(num).toLocaleString()
  }

  const sortDropdown = (
    <ActionMenu>
      <ActionMenu.Button
        leadingVisual={currentSort === SortOrder.Newest ? SortDescIcon : SortAscIcon}
        className="fg-muted"
        variant="invisible"
      >
        <span className="sr-only">Sort by </span>
        {currentSort === SortOrder.Newest ? 'Newest' : 'Oldest'}
        <span className="sr-only">, {currentSort === SortOrder.Newest ? 'descending' : 'ascending'}</span>
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          <ActionList.Group>
            <ActionList.GroupHeading>Sort by:</ActionList.GroupHeading>
            <ActionList.Item
              onSelect={() => onSortChange(SortOrder.Newest)}
              selected={currentSort === SortOrder.Newest}
            >
              <ActionList.LeadingVisual>
                <SortDescIcon />
              </ActionList.LeadingVisual>
              Newest
            </ActionList.Item>
            <ActionList.Item
              onSelect={() => onSortChange(SortOrder.Oldest)}
              selected={currentSort === SortOrder.Oldest}
            >
              <ActionList.LeadingVisual>
                <SortAscIcon />
              </ActionList.LeadingVisual>
              Oldest
            </ActionList.Item>
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )

  const table = (
    <div>
      <ListView
        variant="default"
        /* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */
        title="Attestations List"
        titleHeaderTag="h3"
        className={`border ${pageCount > 1 ? 'rounded-top-2' : 'rounded'}`}
        isSelectable={canDeleteAttestations}
        metadata={
          <ListViewMetadata
            title={`${formatNumber(totalCount)} ${totalCount === 1 ? 'item' : 'items'}`}
            onToggleSelectAll={isSelectAllChecked => {
              return isSelectAllChecked
                ? onAttestationSelectionChange?.(new Set(attestations.map(attestation => attestation.id)))
                : onAttestationSelectionChange?.(new Set())
            }}
          >
            {selectedAttestationIds?.size === 0 && sortDropdown}
            {(selectedAttestationIds?.size || 0) > 0 && (
              <Button variant="danger" leadingVisual={TrashIcon} onClick={() => setDialogOpen(true)}>
                Delete
              </Button>
            )}
          </ListViewMetadata>
        }
      >
        {attestations.map(attestation => (
          <AttestationListItem
            key={attestation.id}
            repo={repo}
            attestation={attestation}
            selected={selectedAttestationIds?.has(attestation.id) || false}
            onSelect={() => onAttestationItemSelect(attestation.id)}
          />
        ))}
      </ListView>
    </div>
  )

  return (
    <div>
      {table}
      {pageCount > 1 && (
        <div className={styles.Box}>
          <Pagination
            pageCount={pageCount}
            currentPage={currentPage}
            marginPageCount={1}
            onPageChange={(e, pageIndex) => {
              e.preventDefault()
              window.scrollTo({top: 0})
              onPageChange(pageIndex)
            }}
            showPages={false}
          />
        </div>
      )}
      {isDialogOpen && (
        <ConfirmationDialog
          title={<span className="f5">Delete attestations</span>}
          confirmButtonContent="Delete attestations"
          confirmButtonType="danger"
          cancelButtonContent="Keep attestations"
          onClose={handleDialogClose}
          width="large"
        >
          This attestation will be deleted permanently. You will not be able to restore the attestation or verify
          artifacts associated with it. For auditing purposes, it is recommended to download a record of selected
          attestations before deleting.
        </ConfirmationDialog>
      )}
    </div>
  )
}

const BlankSlate = () => {
  return (
    <div className={styles.Box_1}>
      <div className="blankslate">
        <Octicon icon={VerifiedIcon} size={30} className={styles.Octicon} />
        <Heading as="h2" className={clsx('blankslate-heading', styles.Heading)}>
          No attestations
        </Heading>
        <p>
          Create attestations using the{' '}
          <Link inline href="https://github.com/actions/attest-build-provenance" target="_blank">
            attest-build-provenance
          </Link>{' '}
          action
        </p>
      </div>
    </div>
  )
}
